import React from 'react';
import { Link } from 'react-router-dom';
import { SearchXIcon, ArrowRightIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
import { degreeCategories } from '../data/degrees';

/**
 * Unmatched URLs previously rendered the homepage, which returns real content
 * at an address that does not exist — a soft 404 that pulls junk URLs into the
 * index. This page is explicitly noindex and routes people back to the pages
 * that do exist.
 */
export function NotFoundPage() {
  usePageMeta('Page Not Found | findonlinecollege.com', 'The page you were looking for could not be found. Browse accredited online degree programs instead.', {
    noindex: true
  });
  const popularCategories = degreeCategories.slice(0, 6);
  return <main className="flex-grow bg-surface" data-id="element-2137" data-mp="3bqnne5k7i6v2">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 py-16 md:py-24 text-center" data-id="element-2138" data-mp="1mcscoqvpwo8b">
        <span aria-hidden="true" className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-background-alt text-primary ring-1 ring-inset ring-hairline" data-id="element-2139" data-mp="1hf8vpj7mxxco">
          <SearchXIcon className="h-7 w-7" data-id="element-2140" data-mp="10csuccnnzwas" />
        </span>

        <p className="mt-6 text-sm font-semibold text-ink-secondary" data-id="element-2141" data-mp="10b7woe61zx8p">
          Error 404
        </p>
        <h1 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-tight text-ink" data-id="element-2142" data-mp="15aue6zswzznb">
          We couldn't find that page
        </h1>
        <p className="mt-4 text-base md:text-lg text-ink-secondary leading-relaxed" data-id="element-2143" data-mp="1f39vrw0aum7o">
          The link may be out of date or mistyped. Start from the homepage, or
          jump straight to a field of study below.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-2144" data-mp="1o3ojf20qkdec">
          <Link to="/" className="inline-flex items-center justify-center h-[52px] px-8 bg-primary hover:bg-primary-hover active:bg-secondary text-white font-semibold rounded-full text-base leading-none shadow-ds-md transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary" data-id="element-2145" data-mp="1t1py7ar4pqk0">
            Back to home
          </Link>
          <Link to="/colleges" className="inline-flex items-center justify-center h-[52px] px-6 rounded-full border border-primary text-[15px] font-semibold text-primary bg-surface hover:bg-background-alt transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary" data-id="element-2146" data-mp="1azsyxkaa9kjh">
            Browse partner colleges
          </Link>
        </div>

        <section aria-labelledby="popular-categories" className="mt-14" data-id="element-2147" data-mp="1tpshrpfj63eg">
          <h2 id="popular-categories" className="text-sm font-semibold text-ink-secondary" data-id="element-2148" data-mp="1pogptd96p633">
            Popular fields of study
          </h2>
          <ul className="mt-4 flex flex-wrap justify-center gap-2" data-id="element-2149" data-mp="1ypna7xeyf11m">
            {popularCategories.map(category => <li key={category.id} data-id="element-2150" data-mp="2iax4vk9lp7pl">
                <Link to={`/degrees/${category.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-background-alt px-4 py-2 text-sm font-medium text-ink hover:border-primary hover:text-primary transition-colors duration-150 ease-out" data-id="element-2151" data-mp="2ibyh3s2cs08w">
                  {category.name}
                  <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2152" data-mp="1zkfl456qxmgd" />
                </Link>
              </li>)}
          </ul>
        </section>
      </div>
    </main>;
}